import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Switch,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { addEditPoste, getPostesSettings, removePoste, PROTECTED_POSTE_IDS } from "../settingsSlice";
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs from "../../../app/lang/langs";
import AddPosteModal from "../Modals/AddPosteModal";

const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

export default function MobilePostesTab({ mobileAddRef }) {
  const dispatch = useDispatch();
  const postesSlice = useSelector((state) => state.settings.postes);

  const [postes, setPostes] = useState([]);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [onEditPost, setOnEditPoste] = useState(null);
  const [onEditName, setOnEditName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setPostes(postesSlice);
  }, [postesSlice]);

  useEffect(() => {
    dispatch(getPostesSettings());
  }, []);

  useEffect(() => {
    if (mobileAddRef) {
      mobileAddRef.current = () => setAddModalVisible(true);
      return () => {
        mobileAddRef.current = null;
      };
    }
  }, [mobileAddRef]);

  const handleActivatePoste = (e, rowData) => {
    const newActive = e.target.checked ? 1 : 0;
    setPostes(
      postes.map((p) =>
        p.id === rowData.id ? { ...p, active: newActive } : p
      )
    );
    dispatch(addEditPoste({ id: rowData.id, active: newActive })).then(
      (actionResponse) => {
        const { responseData, responseError } = actionResponse.payload || {};
        if (responseData && responseData.status) {
          dispatch(getPostesSettings());
          newActive
            ? showSuccessToast("Poste activé")
            : showErrorToast("Poste désactivé");
        } else if (responseData && responseData.status === 0) {
          showErrorToast(responseData.message);
        } else if (responseError) {
          showErrorToast(responseError.data?.error || responseError.statusText);
        }
      }
    );
  };

  const handleEditPoste = (name, id) => {
    if (name.trim() !== "" && id != null) {
      dispatch(addEditPoste({ id: id, name: name }));
      setPostes((current) =>
        current.map((poste) =>
          poste.id === id ? { ...poste, name: name } : poste
        )
      );
      setOnEditPoste(null);
      setOnEditName("");
    }
  };

  const handleDelete = (id) => {
    dispatch(removePoste({ id: id })).then((actionResponse) => {
      const { responseData, responseError } = actionResponse.payload || {};
      if (responseData && responseData.status) {
        dispatch(getPostesSettings());
        showSuccessToast(responseData.message);
      } else if (responseData && responseData.status === 0) {
        showErrorToast(responseData.message);
      } else if (responseError) {
        showErrorToast(responseError.data?.error || responseError.statusText);
      }
    });
  };

  return (
    <Box sx={{ width: "100%", mt: 0, pb: 2, px: 0.5, pt: 1 }}>
      {postes.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <Typography
            sx={{
              color: "rgba(0,0,0,0.35)",
              fontSize: "0.9rem",
              fontWeight: 500,
            }}
          >
            <Langs str="NO_DATA" />
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {postes.map((poste, idx) => (
            <Box
              key={poste.id}
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#ffffff",
                borderRadius: "12px",
                boxShadow:
                  "0 1px 3px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03)",
                px: 1.5,
                py: 0.75,
                gap: 1,
                minHeight: 56,
                transition: TRANSITION_SMOOTH,
                animation: `fadeSlideIn 0.3s ease ${idx * 30}ms both`,
                "@keyframes fadeSlideIn": {
                  from: { opacity: 0, transform: "translateY(8px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
                ...(poste.active !== 1 && {
                  opacity: 0.65,
                }),
              }}
            >
              {/* Toggle switch */}
              <Switch
                checked={poste.active === 1}
                onChange={(e) => handleActivatePoste(e, poste)}
                size="small"
                sx={{
                  flexShrink: 0,
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#FF9015",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#FF9015",
                  },
                }}
              />

              {/* Name area — inline edit or label */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {poste.id === onEditPost ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TextField
                      value={onEditName}
                      onChange={(e) => setOnEditName(e.target.value)}
                      size="small"
                      autoFocus
                      inputProps={{ style: { fontSize: "16px" } }}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#FF9015",
                          },
                        },
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleEditPoste(onEditName, onEditPost);
                        if (e.key === "Escape") {
                          setOnEditPoste(null);
                          setOnEditName("");
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setOnEditPoste(null);
                        setOnEditName("");
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: "rgba(0,0,0,0.04)",
                        borderRadius: "10px",
                        transition: TRANSITION_SMOOTH,
                        "&:active": { transform: "scale(0.93)" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 18, color: "#666" }} />
                    </IconButton>
                    {onEditName.trim() !== "" && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditPoste(onEditName, onEditPost)}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: "rgba(76, 175, 80, 0.1)",
                          border: "1px solid rgba(76, 175, 80, 0.25)",
                          borderRadius: "10px",
                          transition: TRANSITION_SMOOTH,
                          "&:active": { transform: "scale(0.93)" },
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 18, color: "#4caf50" }} />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.925rem",
                      fontWeight: 500,
                      color: poste.active === 1 ? "rgba(0,0,0,0.82)" : "rgba(0,0,0,0.45)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {poste.name}
                  </Typography>
                )}
              </Box>

              {/* Edit + Delete buttons — only visible when not in edit mode for this row */}
              {poste.id !== onEditPost && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setOnEditPoste(poste.id);
                      setOnEditName(poste.name);
                    }}
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: "rgba(255, 144, 21, 0.08)",
                      border: "1px solid rgba(255, 144, 21, 0.18)",
                      borderRadius: "10px",
                      transition: TRANSITION_SMOOTH,
                      "&:active": {
                        bgcolor: "rgba(255, 144, 21, 0.2)",
                        transform: "scale(0.93)",
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18, color: "#FF9015" }} />
                  </IconButton>
                  {!PROTECTED_POSTE_IDS.includes(Number(poste.id)) && (
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(poste)}
                      sx={{
                        width: 38,
                        height: 38,
                        bgcolor: "rgba(211, 47, 47, 0.06)",
                        border: "1px solid rgba(211, 47, 47, 0.15)",
                        borderRadius: "10px",
                        transition: TRANSITION_SMOOTH,
                        "&:active": {
                          bgcolor: "rgba(211, 47, 47, 0.15)",
                          transform: "scale(0.93)",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "18px",
            bgcolor: "#ffffff",
            color: "rgba(0,0,0,0.87)",
            mx: 2,
            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "rgba(0,0,0,0.87)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            pb: 0.5,
          }}
        >
          <WarningAmberIcon sx={{ color: "#d32f2f", fontSize: 22 }} />
          <Langs str="DELETE_POSTE" />
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "rgba(0,0,0,0.65)", fontSize: "0.9rem", lineHeight: 1.6 }}>
            <Langs str="DELETE_POSTE_MESSAGE" />{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "rgba(0,0,0,0.82)" }}>
              {deleteTarget?.name}
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            variant="outlined"
            sx={{
              color: "rgba(0,0,0,0.55)",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              borderColor: "rgba(0,0,0,0.15)",
              minHeight: 44,
              px: 2.5,
              "&:hover": { borderColor: "rgba(0,0,0,0.3)" },
            }}
          >
            <Langs str="CANCEL" />
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={() => {
              handleDelete(deleteTarget.id);
              setDeleteTarget(null);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              minHeight: 44,
              px: 2.5,
              boxShadow: "0 2px 8px rgba(211,47,47,0.25)",
              "&:active": { transform: "scale(0.97)" },
            }}
          >
            <Langs str="DELETE" />
          </Button>
        </DialogActions>
      </Dialog>

      <AddPosteModal visible={addModalVisible} setVisible={setAddModalVisible} />
    </Box>
  );
}
